import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";
import ReactFlow, {
  applyNodeChanges,
  applyEdgeChanges,
  Background,
  Controls,
  Handle,
  MiniMap,
  Position,
} from "reactflow";
import "reactflow/dist/style.css";
import { useIntl } from "react-intl";
import {
  fetchSuccessFactorsByBusinessDomain,
  fetchFinancialGoals, fetchCustomerValues,
  fetchStrategicGoals, fetchStrategicIssues,
  fetchHumanCapitalGoals,
  fetchInfoCapitalGoalsApp, fetchInfoCapitalGoalsData, fetchInfoCapitalGoalsIt,
  fetchOrgCapitalGoals,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

// ─── myCompany 戦略マップの視点定義 ──────────────────────────
// 因果連鎖: L&G → strategic(StrategicIssue/戦略目標) → productValue(StrategicGoal/製品価値) → customer → financial
const PERSPECTIVE = {
  financial:      { label: "財務目標",         bg: "#c62828", color: "#fff" },
  customer:       { label: "顧客価値",          bg: "#f9a825", color: "#212121" },
  productValue:   { label: "製品価値",          bg: "#f57f17", color: "#fff" },
  strategic:      { label: "戦略目標",          bg: "#2e7d32", color: "#fff" },
  humanCapital:   { label: "人的資本目標",      bg: "#1565c0", color: "#fff" },
  infoCapitalApp: { label: "情報資本目標(App)", bg: "#1976d2", color: "#fff" },
  infoCapitalData:{ label: "情報資本目標(Data)",bg: "#0288d1", color: "#fff" },
  infoCapitalIt:  { label: "情報資本目標(IT)",  bg: "#0097a7", color: "#fff" },
  orgCapital:     { label: "組織資本目標",      bg: "#00695c", color: "#fff" },
  default:        { label: "その他",            bg: "#546e7a", color: "#fff" },
};

// ─── ID セットで視点を分類するヘルパー ────────────────────────
function classifyById(id, idSets) {
  for (const [perspective, ids] of Object.entries(idSets)) {
    if (ids.has(id)) return perspective;
  }
  return "default";
}

function toIdSet(res) {
  if (res.status !== "fulfilled" || !Array.isArray(res.value)) return new Set();
  return new Set(res.value.map((f) => f.successFactorId).filter(Boolean));
}

// ─── レイアウト計算 ─────────────────────────────────────────
// 6レイヤー構造（上=結果→下=原因）:
// 財務 → 顧客 → 製品価値 → 戦略目標 → 人的資本/情報資本(1行) → 組織資本
const LAYER_ORDER = [
  { perspectives: ["financial"] },
  { perspectives: ["customer"] },
  { perspectives: ["productValue"] },
  { perspectives: ["strategic"] },
  { perspectives: ["default"] },   // 通常は空、スキップされる
  { perspectives: ["humanCapital", "infoCapitalApp", "infoCapitalData", "infoCapitalIt"] },
  { perspectives: ["orgCapital"] },
];

const NODE_WIDTH  = 180;
const NODE_HEIGHT = 60;
const H_GAP = 60;
const V_GAP_INNER = 140; // 同一視点内の縦間隔（因果関係がある場合）
const LAYER_GAP   = 60; // 層間の最小ギャップ（px）
const START_Y     = 50; // チャート上端のY座標

// 同一視点内の因果関係をトポロジカルソートしてレベルを計算
function computeInnerLevels(group, edgePairs) {
  const nodeIds = new Set(group.map((n) => n.id));
  const levelMap = {};
  const adj = {};
  const inDegree = {};
  group.forEach((n) => {
    levelMap[n.id] = 0;
    adj[n.id] = [];
    inDegree[n.id] = 0;
  });
  edgePairs.forEach(({ source, target }) => {
    if (nodeIds.has(source) && nodeIds.has(target)) {
      adj[source].push(target);
      inDegree[target] = (inDegree[target] || 0) + 1;
    }
  });
  // Kahn's algorithm でトポロジカルレベルを計算
  const queue = group.filter((n) => inDegree[n.id] === 0).map((n) => n.id);
  while (queue.length > 0) {
    const id = queue.shift();
    for (const next of adj[id]) {
      levelMap[next] = Math.max(levelMap[next], levelMap[id] + 1);
      inDegree[next]--;
      if (inDegree[next] === 0) queue.push(next);
    }
  }
  return levelMap;
}

function buildLayout(nodes, edgePairs = []) {
  const byPerspective = {};
  Object.keys(PERSPECTIVE).forEach((k) => { byPerspective[k] = []; });
  nodes.forEach((n) => {
    const p = n.data.perspective;
    if (byPerspective[p]) byPerspective[p].push(n);
    else byPerspective.default.push(n);
  });

  const positioned = [];
  let currentY = START_Y;
  // 配置済みノードの中心X座標（クロスレイヤーバリセンター計算に使用）
  const placedX = {};

  // 各ノードが属するレイヤーインデックスを事前計算（スキップエッジ判定に使用）
  const nodeLayerIndex = {};
  LAYER_ORDER.forEach((layer, idx) => {
    layer.perspectives.forEach((p) => {
      (byPerspective[p] ?? []).forEach((n) => { nodeLayerIndex[n.id] = idx; });
    });
  });

  // クロスレイヤーバリセンター（最上位レイヤー優先 = topmost-first）
  // 財務（layer 0）側から近い方向へ探索し、最初に直接接続が見つかった
  // レイヤーのX平均を使用する。
  // これにより StrategicIssue→CustomerValue のような多層スキップ接続も
  // CustomerValue の真下に配置できる（ProductValue 経由の近接接続に邪魔されない）
  const crossLayerBC = (n, currentLayerIdx) => {
    for (let tIdx = 0; tIdx < currentLayerIdx; tIdx++) {
      const xs = edgePairs
        .filter((e) => e.source === n.id &&
                       nodeLayerIndex[e.target] === tIdx &&
                       placedX[e.target] !== undefined)
        .map((e) => placedX[e.target]);
      if (xs.length > 0) return xs.reduce((a, b) => a + b, 0) / xs.length;
    }
    return null;
  };

  LAYER_ORDER.forEach((layer, currentLayerIdx) => {
    const allLayerNodes = layer.perspectives.flatMap((p) => byPerspective[p] ?? []);
    if (allLayerNodes.length === 0) return;

    let layerBottomY = currentY;

    if (layer.perspectives.length > 1) {
      // 複数視点: 最近接レイヤーBCでソートして1行に横並び
      const layerNodes = [...allLayerNodes];
      layerNodes.sort((a, b) => {
        const ca = crossLayerBC(a, currentLayerIdx) ?? Infinity;
        const cb = crossLayerBC(b, currentLayerIdx) ?? Infinity;
        return ca - cb;
      });
      const rowWidth = layerNodes.length * NODE_WIDTH + (layerNodes.length - 1) * H_GAP;
      layerNodes.forEach((n, i) => {
        const x = -rowWidth / 2 + i * (NODE_WIDTH + H_GAP);
        positioned.push({
          ...n, width: NODE_WIDTH, height: NODE_HEIGHT,
          position: { x, y: currentY },
        });
        placedX[n.id] = x + NODE_WIDTH / 2;
      });
      layerBottomY = currentY + NODE_HEIGHT;
    } else {
      // 単一視点: V_GAP_INNERで因果レベルに応じてY分散
      const p = layer.perspectives[0];
      const group = byPerspective[p] ?? [];
      const levelMap = computeInnerLevels(group, edgePairs);
      const maxLevel = Math.max(0, ...group.map((n) => levelMap[n.id] ?? 0));

      // レベル別にノードをグループ化（キー=level番号）
      const byLevel = {};
      group.forEach((n) => {
        const lv = levelMap[n.id] ?? 0;
        if (!byLevel[lv]) byLevel[lv] = [];
        byLevel[lv].push(n);
      });

      // 同一視点内エッジ（イントラレイヤー）
      const nodeIds = new Set(group.map((n) => n.id));
      const innerEdges = edgePairs.filter(
        ({ source, target }) => nodeIds.has(source) && nodeIds.has(target)
      );
      const nodeColIndex = {}; // nodeId → 列インデックス（イントラBC用）
      const sortedLevels = Object.keys(byLevel).map(Number).sort((a, b) => a - b);

      // フォワードパス: 最近接レイヤーBC優先、フォールバックでイントラBC
      sortedLevels.forEach((lv) => {
        const lvNodes = byLevel[lv];
        lvNodes.sort((a, b) => {
          const ca = crossLayerBC(a, currentLayerIdx);
          const cb = crossLayerBC(b, currentLayerIdx);
          if (ca !== null && cb !== null) return ca - cb;
          if (ca !== null) return -1;
          if (cb !== null) return 1;
          const intraBC = (nd) => {
            const idxs = innerEdges
              .filter((e) => e.target === nd.id)
              .map((e) => nodeColIndex[e.source] ?? -1)
              .filter((i) => i >= 0);
            return idxs.length === 0 ? Infinity : idxs.reduce((s, v) => s + v, 0) / idxs.length;
          };
          return intraBC(a) - intraBC(b);
        });
        lvNodes.forEach((n, i) => { nodeColIndex[n.id] = i; });
      });

      // バックワードパス: 最近接レイヤーBC優先、フォールバックでイントラBC（後継位置基準）
      [...sortedLevels].reverse().forEach((lv) => {
        if (lv === sortedLevels[sortedLevels.length - 1]) return;
        const lvNodes = byLevel[lv];
        lvNodes.sort((a, b) => {
          const ca = crossLayerBC(a, currentLayerIdx);
          const cb = crossLayerBC(b, currentLayerIdx);
          if (ca !== null && cb !== null) return ca - cb;
          if (ca !== null) return -1;
          if (cb !== null) return 1;
          const intraBC = (nd) => {
            const idxs = innerEdges
              .filter((e) => e.source === nd.id)
              .map((e) => nodeColIndex[e.target] ?? -1)
              .filter((i) => i >= 0);
            return idxs.length === 0 ? Infinity : idxs.reduce((s, v) => s + v, 0) / idxs.length;
          };
          return intraBC(a) - intraBC(b);
        });
        lvNodes.forEach((n, i) => { nodeColIndex[n.id] = i; });
      });

      // ソート済み順序でX・Y座標を確定して配置
      sortedLevels.forEach((lv) => {
        const lvNodes = byLevel[lv];
        const visualOffset = (maxLevel - lv) * V_GAP_INNER;
        const nodeY = currentY + visualOffset;
        const rowWidth = lvNodes.length * NODE_WIDTH + (lvNodes.length - 1) * H_GAP;
        lvNodes.forEach((n, i) => {
          const x = -rowWidth / 2 + i * (NODE_WIDTH + H_GAP);
          positioned.push({
            ...n, width: NODE_WIDTH, height: NODE_HEIGHT,
            position: { x, y: nodeY },
          });
          placedX[n.id] = x + NODE_WIDTH / 2;
        });
        layerBottomY = Math.max(layerBottomY, nodeY + NODE_HEIGHT);
      });
    }

    currentY = layerBottomY + LAYER_GAP;
  });

  return positioned;
}

// ─── カスタムノードコンポーネント ──────────────────────────
function StrategyNode({ data }) {
  const p = PERSPECTIVE[data.perspective] ?? PERSPECTIVE.default;
  return (
    <>
      {/* エッジ接続用ハンドル（非表示） */}
      <Handle type="target" position={Position.Bottom} style={{ opacity: 0, pointerEvents: "none" }} />
      <Handle type="source" position={Position.Top}    style={{ opacity: 0, pointerEvents: "none" }} />
    <Tooltip
      title={
        data.description ? (
          <Box sx={{ maxWidth: 280, p: 0.5 }}>
            <Typography variant="caption">{data.description}</Typography>
          </Box>
        ) : ""
      }
      arrow
      placement="top"
    >
      <Box
        sx={{
          width: NODE_WIDTH,
          height: NODE_HEIGHT,
          borderRadius: 2,
          bgcolor: p.bg,
          color: p.color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          px: 1,
          fontSize: 12,
          fontWeight: 600,
          cursor: "default",
          boxShadow: 3,
          lineHeight: 1.3,
          userSelect: "none",
        }}
      >
        {data.label}
      </Box>
    </Tooltip>
    </>
  );
}

const nodeTypes = { strategyNode: StrategyNode };

// ─── メインページ ────────────────────────────────────────
export default function StrategyMapChartPage() {
  const { businessDomainId } = useParams();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [loading, setLoading] = useState(false);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);

  // "dimensions" 変更はスキップしてドラッグのみ適用（無限ループ防止）
  const onNodesChange = useCallback(
    (changes) => {
      const filtered = changes.filter((c) => c.type !== "dimensions");
      if (filtered.length > 0) setNodes((nds) => applyNodeChanges(filtered, nds));
    },
    []
  );
  const onEdgesChange = useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    []
  );

  const fetchData = useCallback(async () => {
    if (!businessDomainId) return;
    setLoading(true);
    try {
      const allFactors = await fetchSuccessFactorsByBusinessDomain(businessDomainId);

      // 全カテゴリの ID セットを並列取得
      // strategicGoal = 製品価値 (StrategicGoalDto.productTypeId あり)
      // strategicIssue = 戦略目標 (StrategicIssueDto.businessProcessId あり)
      const [
        financialRes, customerRes, productValueRes, strategicRes,
        humanCapRes,
        infoAppRes, infoDataRes, infoItRes,
        orgCapRes,
      ] = await Promise.allSettled([
        fetchFinancialGoals(businessDomainId),
        fetchCustomerValues(businessDomainId),
        fetchStrategicGoals(businessDomainId),
        fetchStrategicIssues(businessDomainId),
        fetchHumanCapitalGoals(businessDomainId),
        fetchInfoCapitalGoalsApp(businessDomainId),
        fetchInfoCapitalGoalsData(businessDomainId),
        fetchInfoCapitalGoalsIt(businessDomainId),
        fetchOrgCapitalGoals(businessDomainId),
      ]);

      const idSets = {
        financial:       toIdSet(financialRes),
        customer:        toIdSet(customerRes),
        productValue:    toIdSet(productValueRes),  // StrategicGoal = 製品価値
        strategic:       toIdSet(strategicRes),      // StrategicIssue = 戦略目標
        humanCapital:    toIdSet(humanCapRes),
        infoCapitalApp:  toIdSet(infoAppRes),
        infoCapitalData: toIdSet(infoDataRes),
        infoCapitalIt:   toIdSet(infoItRes),
        orgCapital:      toIdSet(orgCapRes),
      };

      // ノード生成
      const rawNodes = allFactors.map((sf) => ({
        id: sf.successFactorId,
        type: "strategyNode",
        data: {
          label: sf.successFactorName,
          description: sf.successFactorDescription ?? "",
          perspective: classifyById(sf.successFactorId, idSets),
        },
        position: { x: 0, y: 0 },
      }));

      // エッジ生成（同一視点内因果関係のレイアウトにも使用）
      // sf（源泉）が原因（下位層）、sf.sources[i] が結果（上位層）
      // エッジ方向: sf（原因・下位層） → src（結果・上位層）
      const edgePairs = [];
      const newEdges = [];
      allFactors.forEach((sf) => {
        if (Array.isArray(sf.sources)) {
          sf.sources.forEach((src) => {
            edgePairs.push({ source: sf.successFactorId, target: src.successFactorId });
            newEdges.push({
              id: `${sf.successFactorId}->${src.successFactorId}`,
              type: "smoothstep",
              source: sf.successFactorId,   // 原因（下位層）
              target: src.successFactorId,  // 結果（上位層）
              animated: false,
              style: { stroke: "#424242", strokeWidth: 2 },
              markerEnd: { type: "arrowclosed", color: "#424242" },
            });
          });
        }
      });

      setNodes(buildLayout(rawNodes, edgePairs));
      setEdges(newEdges);
    } catch (e) {
      notify.apiError(e);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 凡例（default 除外）
  const legend = useMemo(
    () => Object.entries(PERSPECTIVE).filter(([k]) => k !== "default"),
    []
  );

  return (
    <Box sx={{ height: "calc(100vh - 120px)", display: "flex", flexDirection: "column" }}>
      {/* タイトル + 凡例 */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 1, flexWrap: "wrap" }}>
        <Typography variant="h6" sx={{ whiteSpace: "nowrap" }}>{t("tree.smChart")}</Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          {legend.map(([key, val]) => (
            <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: val.bg, flexShrink: 0 }} />
              <Typography variant="caption">{val.label}</Typography>
            </Box>
          ))}
          {/* 因果関係線の凡例 */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 1 }}>
            <Box sx={{ width: 24, height: 2, bgcolor: "#424242" }} />
            <Typography variant="caption">因果関係</Typography>
          </Box>
        </Box>
      </Box>

      {/* チャート本体 */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 10 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ flex: 1, border: "1px solid #e0e0e0", borderRadius: 1, overflow: "hidden" }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.3 }}
            nodesDraggable
            nodesConnectable={false}
            elementsSelectable={false}
          >
            <Background color="#f5f5f5" gap={20} />
            <Controls />
            <MiniMap
              nodeColor={(n) => PERSPECTIVE[n.data?.perspective]?.bg ?? "#546e7a"}
              style={{ background: "#fafafa" }}
            />
          </ReactFlow>
        </Box>
      )}
    </Box>
  );
}
